import { showUserAlert } from "../../utils/userErrorMessage";
import React, { useState, useEffect } from "react";
import { supabase } from "../../supabase/client";
import { createReservationBatch } from "../../services/reservationWrites";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import TimeSelect from "../../components/ui/time-select";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";

const WEEK_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const RESERVATION_YEAR_OPTIONS = Array.from(
  { length: 15 },
  (_, index) => new Date().getFullYear() - 10 + index
);

export default function CommuteForm({ prefill, targetTable = "reservations", reservationMode = "예약" }) {
  const [carSearchResults, setCarSearchResults] = useState([]);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const location = useLocation();

  const initialFormState = {
    year: tomorrow.getFullYear(),
    month: tomorrow.getMonth() + 1,
    day: tomorrow.getDate(),
    nights: 0,               // 여러 날 생성
    time: "",
    arrival_time: "",
    round_trip_time: "",
    start: "",
    transit: "",
    destination: "",
    user: "",
    user_phone: "",
    money: "",
    driver_money: "",
    car_number: "",          // 지정 차량(옵션)
    round: "왕복",
    seat: "대형",
    seat_detail: "",  
    note: "",
  };

  const [form, setForm] = useState(initialFormState);

  // 🔹 휴무 요일(체크 = 휴무/스킵). 0=일 ~ 6=토
  const [offDays, setOffDays] = useState({
    0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false,
  });

  // 🔹 공휴일 포함 여부 (기본: 포함 안 함 = false)
  const [includeHolidays, setIncludeHolidays] = useState(false);
  
  // 기간 계산 유틸
  const ymd = (d) => d.toISOString().split("T")[0];
  const getPlannedRange = () => {
    const base = new Date(`${form.year}-${String(form.month).padStart(2,"0")}-${String(form.day).padStart(2,"0")}`);
    const startYmd = ymd(base);
    const end = new Date(base);
    end.setDate(end.getDate() + parseInt(form.nights || 0));
    const endYmd = ymd(end);
    return { startYmd, endYmd };
  };

  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf) return;

    const d = pf.date ? new Date(pf.date) : null;
    setForm((prev) => ({
      ...prev,
      // 날짜 → 년/월/일
      year: d ? d.getFullYear() : prev.year,
      month: d ? (d.getMonth() + 1) : prev.month,
      day: d ? d.getDate() : prev.day,

      // 공통 필드
      time: pf.time ?? prev.time,
      arrival_time: pf.arrival_time ?? prev.arrival_time,
      round_trip_time: pf.round_trip_time ?? prev.round_trip_time,
      start: pf.start ?? prev.start,
      transit: pf.transit ?? prev.transit,
      destination: pf.destination ?? prev.destination,
      user: pf.user ?? prev.user,
      user_phone: pf.user_phone ?? prev.user_phone,
      money: pf.money ?? prev.money,
      driver_money: pf.driver_money ?? prev.driver_money,
      note: pf.note ?? prev.note,
      seat: pf.seat ?? prev.seat,      
      seat_detail: pf.seat_detail ?? prev.seat_detail,
      round: pf.round ?? prev.round,

      // 일반/학단/투어: 여러 대/박 지원
      buses: pf.buses ?? prev.buses,
      nights: pf.nights ?? prev.nights,

      // 통근/지정차 있을 때만 영향
      car_number: pf.car_number ?? prev.car_number,
    }));
  }, [location.state]);           

  const handleCarNumberChange = async (value) => {
    setForm((prev) => ({ ...prev, car_number: value }));
    if (!value) {
      setCarSearchResults([]);
      return;
    }
    const company = localStorage.getItem("email");
    const { startYmd, endYmd } = getPlannedRange();

    // 1) 기간 겹치는 휴무 기사 목록
    const { data: offs } = await supabase
      .from("holidays_driver")
      .select("driver")
      .eq("company", company)
      .lte("from", endYmd)
      .gte("to", startYmd);
    const offSet = new Set((offs || []).map((o) => o.driver));

    // 2) 차량 + 기사 동시 조회 → 휴무 기사 제외
    const { data, error } = await supabase
      .from("cars")
      .select("car_number_only, driver")
      .eq("company", company)
      .ilike("car_number_only", `%${value}%`);

    if (!error) {
      const filtered = (data || []).filter((c) => !offSet.has(c.driver || ""));
      setCarSearchResults(filtered);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]:
        field === "nights"
          ? String(value).replace(/[^0-9]/g, "")
          : value,
    }));
  };

  const toggleOffDay = (dow) => {
    setOffDays((prev) => ({ ...prev, [dow]: !prev[dow] }));
  };

  const handleSubmit = async () => {
    const companyId = localStorage.getItem("email");
    const companyName = localStorage.getItem("companyName") || companyId;
    const baseDate = new Date(
      `${form.year}-${String(form.month).padStart(2, "0")}-${String(form.day).padStart(2, "0")}`
    );
    const groupId = uuidv4();

    // 공휴일 세트 조회
    const { data: holidays } = await supabase
      .from("holidays")
      .select("holiday_date")          // ← 당신의 스키마에 맞춰 컬럼명 확인
      .eq("company", companyId);
    const holidaySet = new Set((holidays || []).map((h) => h.holiday_date));

    // ✅ 지정차량으로 기사정보 가져오기 (+ 휴무 안전장치)
    let driverInfo = { driver: "", phone: "" };
    if (form.car_number) {
      const { data: carData } = await supabase
        .from("cars")
        .select("driver, phone")
        .eq("company", companyId)
        .eq("car_number_only", form.car_number)
        .maybeSingle();

      if (carData) {
        // 선택 기간과 겹치면 차단
        const base = new Date(`${form.year}-${String(form.month).padStart(2,"0")}-${String(form.day).padStart(2,"0")}`);
        const startYmd = ymd(base);
        const end = new Date(base);
        end.setDate(end.getDate() + parseInt(form.nights || 0));
        const endYmd = ymd(end);

        const { data: off1 } = await supabase
          .from("holidays_driver")
          .select("id")
          .eq("company", companyId)
          .eq("driver", carData.driver || "")
          .lte("from", endYmd)
          .gte("to", startYmd);
        if (off1 && off1.length > 0) {
          showUserAlert("선택 기간에 휴무로 등록된 기사입니다. 다른 차량/기사를 선택하세요.");
          return;
        }
        driverInfo = { driver: carData.driver, phone: carData.phone };
      }
    }

    const nights = parseInt(form.nights || "0", 10);

    const reservations = [];
    for (let d = 0; d <= nights; d++) {
      const cur = new Date(baseDate);
      cur.setDate(baseDate.getDate() + d);

      // 휴무 요일이면 스킵
      const dow = cur.getDay(); // 0=일 ~ 6=토
      if (offDays[dow]) continue;

      // 공휴일 스킵(포함 체크 안 되어 있으면)
      const ymd = `${cur.getFullYear()}${String(cur.getMonth() + 1).padStart(2, "0")}${String(cur.getDate()).padStart(2, "0")}`;
      if (!includeHolidays && holidaySet.has(ymd)) continue;

      const formattedDate = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;

      reservations.push({
        date: formattedDate,
        time: form.time || null,
        arrival_time: form.arrival_time || null,
        round_trip_time: form.round === "왕복" ? form.round_trip_time || null : null,
        start: form.start,
        transit: form.transit,
        destination: form.destination,
        user: form.user,
        user_phone: form.user_phone,
        money: reservations.length === 0 ? parseInt(form.money) || 0 : 0,
        driver_money: parseInt(form.driver_money) || 0,
        memo: form.note,
        one_round: form.round,
        number_of_car: 1,               // 통근은 1대씩
        days: nights,
        car_number: form.car_number || "",
        human_name: driverInfo.driver,
        phone: driverInfo.phone,
        reservation_type: "통근",
        company: companyId,
        company_name: companyName,
        seat: form.seat,
        seat_detail: form.seat_detail || null, 
        reservation_group_id: groupId,
        // 필요하면 off 요일/공휴일 포함 여부를 보관할 수도 있음
        // off_days: offDays,
        // include_holidays: includeHolidays,
      });
    }

    if (reservations.length === 0) {
      showUserAlert("등록할 예약이 없습니다. 요일/휴일 설정을 확인하세요.");
      return;
    }

    // summary 계산
    const baseDate2 = new Date(reservations[0].date);
    const endDateObj = new Date(baseDate2);
    endDateObj.setDate(endDateObj.getDate() + nights);

    const formatDate = (date) => date.toISOString().split("T")[0];

    const summaryData = {
      group_id: groupId,
      start_date: formatDate(baseDate2),
      end_date: formatDate(endDateObj),
      days: nights,
      time: form.time || null,
      arrival_time: form.arrival_time || null,
      round_trip_time: form.round === "왕복" ? form.round_trip_time || null : null,
      number_of_car: 1,               // 통근 기본 1
      user: form.user,
      user_phone: form.user_phone,
      reservation_type: "통근",
      car_type: form.seat,
      start: form.start,
      destination: form.destination,
      transit: form.transit,
      money: parseInt(form.money) || 0,
      driver_money: parseInt(form.driver_money) || 0,
      company: companyId,
      company_name: companyName,
    };

    const { error } = await createReservationBatch(reservations, [summaryData], targetTable);
    if (error) {
      showUserAlert("등록 실패: " + error.message);
      return;
    }

    showUserAlert(`통근 ${reservationMode}이 등록되었습니다.`);
    setForm(initialFormState);
    setCarSearchResults([]);
    setOffDays({ 0:false,1:false,2:false,3:false,4:false,5:false,6:false });
    setIncludeHolidays(false);
  };

  return (
    <div className="px-4 pt-1 pb-4 space-y-2">
      <h2 className="text-lg font-semibold">통근 {reservationMode} 등록</h2>
      <Card className="p-2">
        <CardContent className="space-y-4 bg-white">
          <div className="reservation-form-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="reservation-section-title">운행 정보</div>
            {/* 운행시작일 */}
            <div>
              <label className="block font-semibold mb-1">운행시작일</label>
              <div className="flex gap-2 items-center">
                <select value={form.year} onChange={(e) => handleChange("year", e.target.value)}>
                  {RESERVATION_YEAR_OPTIONS.map((y) => <option key={y}>{y}</option>)}
                </select>
                <select value={form.month} onChange={(e) => handleChange("month", e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m}>{m}</option>)}
                </select>
                <select value={form.day} onChange={(e) => handleChange("day", e.target.value)}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>

            {/* 차량 크기 / 인승 */}
            <div>
              <label className="block font-semibold mb-1">차량 크기/인승</label>
              <div className="flex gap-2">
                <select className="w-24" value={form.seat} onChange={(e) => handleChange("seat", e.target.value)}>
                  {["대형", "중형", "우등"].map((seat) => (
                    <option key={seat} value={seat}>{seat}</option>
                  ))}
                </select>
                <Input
                  type="text"
                  placeholder="예) 45인승"
                  value={form.seat_detail}
                  onChange={(e) => handleChange("seat_detail", e.target.value)}
                />
              </div>
            </div>

            {/* 박 수 */}
            <div>
              <label className="block font-semibold mb-1">박</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  className="w-24"
                  value={form.nights}
                  onChange={(e) => handleChange("nights", e.target.value)}
                />
              </div>

              {/* 요일별 휴무 */}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold">휴무 요일:</span>
                {WEEK_LABELS.map((label, dow) => (
                  <label key={dow} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={!!offDays[dow]}
                      onChange={() => toggleOffDay(dow)}
                    />
                    {label}
                  </label>
                ))}
              </div>

              {/* 공휴일 포함 */}
              <label className="mt-3 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeHolidays}
                  onChange={() => setIncludeHolidays((v) => !v)}
                />
                공휴일 포함
              </label>
            </div>

            {/* 출발시간 */}
            <div>
              <label className="block font-semibold mb-1">출발시간</label>
              <TimeSelect value={form.time} onChange={(value) => handleChange("time", value)} />
            </div>
            <div>
              <label className="block font-semibold mb-1">도착 예상 시간</label>
              <TimeSelect value={form.arrival_time} onChange={(value) => handleChange("arrival_time", value)} />
            </div>
            {form.round === "왕복" && (
              <div>
                <label className="block font-semibold mb-1">회차 시간</label>
                <TimeSelect value={form.round_trip_time} onChange={(value) => handleChange("round_trip_time", value)} />
              </div>
            )}

            <div className="reservation-section-title">경로 정보</div>
            {/* 출발/경유/행선/사용자/노선명 */}
            <div>
              <label className="block font-semibold mb-1">출발지</label>
              <Input value={form.start} onChange={(e) => handleChange("start", e.target.value)} placeholder="출발지" />
            </div>
            <div>
              <label className="block font-semibold mb-1">경유지</label>
              <Input value={form.transit} onChange={(e) => handleChange("transit", e.target.value)} placeholder="경유지" />
            </div>
            <div>
              <label className="block font-semibold mb-1">행선지</label>
              <Input value={form.destination} onChange={(e) => handleChange("destination", e.target.value)} placeholder="행선지" />
            </div>
            <div>
              <label className="block font-semibold mb-1">사용자/노선명</label>
              <Input value={form.user} onChange={(e) => handleChange("user", e.target.value)} placeholder="사용자/노선명" />
            </div>

            {/* 연락처/금액 */}
            <div>
              <label className="block font-semibold mb-1">사용자 전화번호</label>
              <Input value={form.user_phone} onChange={(e) => handleChange("user_phone", e.target.value)} placeholder="전화번호" />
            </div>

            <div className="reservation-section-title">금액 정보</div>
            <div>
              <label className="block font-semibold mb-1">받을 금액</label>
              <Input type="number" value={form.money} onChange={(e) => handleChange("money", e.target.value)} placeholder="받을 금액" />
            </div>
            <div>
              <label className="block font-semibold mb-1">기사 금액</label>
              <Input type="number" value={form.driver_money} onChange={(e) => handleChange("driver_money", e.target.value)} placeholder="기사 금액" />
            </div>

            <div className="reservation-section-title">차량 및 기사</div>
            {/* 지정 차량(자동완성) */}
            <div className="relative">
              <label className="block font-semibold mb-1">지정 차량 번호/운수회사명</label>
              <Input
                value={form.car_number}
                onChange={(e) => handleCarNumberChange(e.target.value)}
                placeholder="지정 차량 번호/운수회사명"
              />
              {carSearchResults.length > 0 && (
                <ul className="absolute z-10 bg-white border rounded w-full max-h-48 overflow-auto">
                  {carSearchResults.map((car, idx) => (
                    <li
                      key={idx}
                      className="p-2 hover:bg-gray-200 cursor-pointer"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, car_number: car.car_number_only }));
                        setCarSearchResults([]);
                      }}
                    >
                      {car.car_number_only}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 편도/왕복 등 */}
            <div>
              <label className="block font-semibold mb-1">편도/왕복</label>
              <select value={form.round} onChange={(e) => handleChange("round", e.target.value)}>
                {["편도", "왕복", "무박", "1박2일", "2박3일", "3박4일", "4박5일", "5박6일", "6박7일"].map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="reservation-section-title">기타</div>
            {/* 기타 메모 */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block font-semibold mb-1">기타</label>
              <Input value={form.note} onChange={(e) => handleChange("note", e.target.value)} placeholder="기타" />
            </div>
          </div>

          <div className="reservation-action-row">
            <Button onClick={handleSubmit}>등록</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
